import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTeamMembers, createInvite, getInvites, removeTeamMember, deleteInvite, getStaffProperties, assignPropertiesToStaff, getCleaningCompanies, getProperties } from '../lib/api';
import { cacheGet, cacheSet } from '../lib/cache';
import { toast } from 'sonner';
import { getUser } from '../lib/auth';
import { Users, UserPlus, Mail, Trash2, Shield, User as UserIcon, Clock, Check, X, Copy, Plus, Search, Phone, BarChart3, Building2, Sparkles, SprayCan } from 'lucide-react';
import MobileNav, { PropertiesIcon } from '../components/MobileNav';
import { motion, AnimatePresence } from 'framer-motion';
import ManagerTeamView from './ManagerTeamView';
import CleaningCompaniesSection from '../components/CleaningCompaniesSection';
import { AssignedPropertiesDropdown } from '../components/AssignedPropertiesDropdown';
import { PropertyPickerModal } from '../components/PropertyPickerModal';
import PullToRefresh from '../components/PullToRefresh';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

export default function TeamPage() {
  const currentUser = getUser();
  if (currentUser?.role === 'company_manager') {
    return <ManagerTeamView />;
  }
  return <AdminStaffTeamPage currentUser={currentUser} />;
}

function AdminStaffTeamPage({ currentUser }) {
  const navigate = useNavigate();
  const [members, setMembers] = useState(cacheGet('team')?.members || []);
  const [invites, setInvites] = useState(cacheGet('team')?.invites || []);
  const [loading, setLoading] = useState(!cacheGet('team'));
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMobile, setInviteMobile] = useState('');
  const [inviteRole, setInviteRole] = useState('staff');
  const [creating, setCreating] = useState(false);
  const [removeUserId, setRemoveUserId] = useState(null);
  const [deleteInviteId, setDeleteInviteId] = useState(null);
  const [lastInviteLink, setLastInviteLink] = useState(null);
  const [assignModal, setAssignModal] = useState(null); // {userId, userName, properties, assignedIds}
  const [savingAssignments, setSavingAssignments] = useState(false);
  const [propertySearch, setPropertySearch] = useState('');
  const [cleaningData, setCleaningData] = useState(null);
  const [showInviteChooser, setShowInviteChooser] = useState(false);
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [allProperties, setAllProperties] = useState([]);
  const [inviteAssignNow, setInviteAssignNow] = useState(false);
  const [inviteProps, setInviteProps] = useState(new Set());
  const [showInvitePicker, setShowInvitePicker] = useState(false);

  const isAdmin = currentUser?.role === 'admin';
  const cleaningEnabled = isAdmin && cleaningData && cleaningData.limit !== 0;

  const loadCleaning = async () => {
    if (currentUser?.role !== 'admin') return;
    try {
      const res = await getCleaningCompanies();
      setCleaningData(res.data);
    } catch (error) {
      // feature may be gated; ignore
    }
  };

  const loadData = async (silent = false) => {
    try {
      const [membersRes, invitesRes] = await Promise.all([
        getTeamMembers(),
        getInvites()
      ]);
      setMembers(membersRes.data.members);
      setInvites(invitesRes.data.invites);
      cacheSet('team', { members: membersRes.data.members, invites: invitesRes.data.invites });
      if (currentUser?.role === 'admin') {
        try {
          const propsRes = await getProperties();
          setAllProperties(propsRes.data.properties || propsRes.data || []);
        } catch (e) { /* ignore */ }
      }
      await loadCleaning();
    } catch (error) {
      if (!silent) toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useAutoRefresh(() => loadData(true));

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const propertyIds = (inviteRole === 'staff' && inviteAssignNow) ? Array.from(inviteProps) : [];
      const response = await createInvite(inviteEmail, inviteName, inviteRole, inviteMobile.trim() || null, propertyIds);
      
      // Show the invite link prominently
      if (response.data.inviteLink) {
        setLastInviteLink(response.data.inviteLink);
        
        // Try to copy to clipboard, but don't fail if it doesn't work
        try {
          await navigator.clipboard.writeText(response.data.inviteLink);
          toast.success('Invite created! Link copied to clipboard.');
        } catch (clipboardError) {
          // Clipboard failed (common on mobile), but invite was still created
          console.log('Clipboard copy failed:', clipboardError);
          toast.success('Invite created successfully!');
        }
      } else {
        toast.success('Invite sent successfully!');
      }
      
      setInviteName('');
      setInviteEmail('');
      setInviteMobile('');
      setInviteRole('staff');
      setInviteAssignNow(false);
      setInviteProps(new Set());
      setShowInviteForm(false);
      loadData();
    } catch (error) {
      console.error('Create invite error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    } finally {
      setCreating(false);
    }
  };

  const copyLastLink = async () => {
    if (lastInviteLink) {
      await navigator.clipboard.writeText(lastInviteLink);
      toast.success('Invite link copied!');
    }
  };

  const handleDeleteInvite = async () => {
    if (!deleteInviteId) return;
    
    try {
      await deleteInvite(deleteInviteId);
      toast.success('Invite cancelled');
      setDeleteInviteId(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete invite');
    }
  };

  const openAssignModal = async (member) => {
    try {
      const response = await getStaffProperties(member.id);
      setAssignModal({
        userId: member.id,
        userName: member.name || member.email,
        properties: response.data.allProperties,
        assignedIds: new Set(response.data.assignedPropertyIds)
      });
    } catch (error) {
      toast.error('Failed to load properties');
    }
  };

  const togglePropertyAssignment = (propertyId) => {
    if (!assignModal) return;
    const newAssigned = new Set(assignModal.assignedIds);
    if (newAssigned.has(propertyId)) {
      newAssigned.delete(propertyId);
    } else {
      newAssigned.add(propertyId);
    }
    setAssignModal({ ...assignModal, assignedIds: newAssigned });
  };

  const toggleInviteProp = (propertyId) => {
    const s = new Set(inviteProps);
    s.has(propertyId) ? s.delete(propertyId) : s.add(propertyId);
    setInviteProps(s);
  };

  const savePropertyAssignments = async () => {
    if (!assignModal) return;
    setSavingAssignments(true);
    try {
      await assignPropertiesToStaff(assignModal.userId, Array.from(assignModal.assignedIds));
      toast.success('Properties assigned! They will receive a notification.');
      setAssignModal(null);
      loadData();
    } catch (error) {
      toast.error('Failed to assign properties');
    } finally {
      setSavingAssignments(false);
    }
  };

  const handleRemoveMember = async () => {
    try {
      await removeTeamMember(removeUserId);
      toast.success('Team member removed');
      setRemoveUserId(null);
      loadData();
    } catch (error) {
      toast.error('Failed to remove team member');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{
        background: 'linear-gradient(135deg, #EFF6FF 0%, #ECFDF5 100%)'
      }}>
        <div className="spinner-3d"></div>
      </div>
    );
  }

  const adminMembers = members.filter((m) => m.role === 'admin');
  const staffMembers = members.filter((m) => m.role === 'staff');

  const memberCard = (member, index) => (
    <motion.div
      key={member.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="card-3d property-card-3d p-6"
      data-testid={`team-member-${member.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0" style={{
            background: member.role === 'admin'
              ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
              : 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
          }}>
            {member.photoUrl ? (
              <img
                src={member.photoUrl}
                alt={member.name || 'Team member'}
                className="w-full h-full object-cover"
                data-testid={`member-photo-${member.id}`}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : member.role === 'admin' ? (
              <Shield size={24} className="text-white" />
            ) : (
              <UserIcon size={24} className="text-white" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">{member.name || 'Team Member'}</h3>
            <p className="text-xs text-slate-500">{member.email}</p>
            {member.mobile && (
              <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                <Phone size={11} className="text-slate-400" />
                <a href={`tel:${member.mobile}`} className="hover:text-emerald-600" data-testid={`member-mobile-${member.id}`}>{member.mobile}</a>
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {member.role === 'admin' && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold" data-testid={`admin-badge-${member.id}`}>
              <Shield size={12} /> Admin
            </span>
          )}
          {isAdmin && member.id !== currentUser?.id && (
            <button
              onClick={() => setRemoveUserId(member.id)}
              className="text-red-400 hover:text-red-600 p-1"
              data-testid={`remove-member-${member.id}`}
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      {member.role === 'staff' && (
        <div className="mb-4">
          <AssignedPropertiesDropdown
            properties={member.assignedProperties || []}
            testid={`assigned-properties-dropdown-${member.id}`}
          />
        </div>
      )}

      <div className="pt-4 border-t border-slate-100">
        {member.role === 'admin' ? (
          isAdmin && (
            <button
              onClick={() => navigate('/team/performance')}
              className="w-full h-11 rounded-xl bg-gradient-to-r from-blue-500 to-emerald-500 text-white text-sm font-bold flex items-center justify-center gap-2 shadow-md hover:shadow-lg active:scale-[0.98] transition-all"
              data-testid="full-team-performance"
            >
              <BarChart3 size={16} />
              Full Team Performance
            </button>
          )
        ) : (
          <div className="flex items-center justify-between">
            <span className="badge-3d bg-green-50 text-green-700 border-green-200">Staff</span>
            {isAdmin && (
              <button
                onClick={() => openAssignModal(member)}
                className="btn-3d h-9 px-3 rounded-xl bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-200 text-sm font-semibold flex items-center gap-1"
                data-testid={`assign-properties-${member.id}`}
              >
                Manage Properties
              </button>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );

  return (
    <PullToRefresh onRefresh={() => loadData()}>
    <div className="min-h-screen flex flex-col" style={{
      background: 'linear-gradient(135deg, #EFF6FF 0%, #ECFDF5 100%)'
    }}>
      <MobileNav />

      <main className="flex-1 mobile-container pb-28 md:pb-8">
        <div className="flex items-center justify-between mb-8 animate-slide-in">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-2">Team</h1>
            <p className="text-slate-600 font-medium">Manage your team members</p>
          </div>
          {isAdmin && (
            <button
              onClick={() => (cleaningEnabled ? setShowInviteChooser(true) : setShowInviteForm(!showInviteForm))}
              className="btn-3d btn-3d-accent h-14 px-6 rounded-2xl inline-flex items-center justify-center"
              data-testid="invite-button"
            >
              <UserPlus size={20} className="mr-0 sm:mr-2" />
              <span className="hidden sm:inline">Invite</span>
            </button>
          )}
        </div>

        {/* Last Invite Link - Show prominently */}
        {lastInviteLink && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-3d p-6 mb-8 border-2 border-emerald-200 bg-emerald-50"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
                <Mail size={20} className="text-white" />
              </div>
              <h3 className="text-lg font-bold text-emerald-800">Invite Link Created!</h3>
            </div>
            <p className="text-sm text-emerald-700 mb-3">Share this link with the person you're inviting:</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={lastInviteLink}
                readOnly
                className="input-3d flex-1 bg-white text-sm font-mono"
              />
              <button
                onClick={copyLastLink}
                className="btn-3d btn-3d-accent h-12 px-4 rounded-xl"
              >
                <Copy size={18} />
              </button>
            </div>
            <button
              onClick={() => setLastInviteLink(null)}
              className="mt-3 text-sm text-emerald-600 hover:text-emerald-800"
            >
              Dismiss
            </button>
          </motion.div>
        )}

        {/* Invite Form */}
        {showInviteForm && isAdmin && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-3d p-6 mb-8"
          >
            <h3 className="text-2xl font-bold gradient-text mb-6">Invite Team Member</h3>
            <form onSubmit={handleCreateInvite} className="space-y-4" data-testid="invite-form">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Name</label>
                <input
                  type="text"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="John Doe"
                  required
                  className="input-3d w-full"
                  data-testid="invite-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Email</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="john@example.com"
                  required
                  className="input-3d w-full"
                  data-testid="invite-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Mobile number <span className="text-slate-400 font-normal">(optional)</span></label>
                <input
                  type="tel"
                  value={inviteMobile}
                  onChange={(e) => setInviteMobile(e.target.value)}
                  placeholder="+44 7123 456789"
                  className="input-3d w-full"
                  data-testid="invite-mobile-input"
                />
                <p className="text-xs text-slate-400 mt-1.5">Handy for a quick direct contact. Not shared publicly.</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="input-3d w-full"
                  data-testid="invite-role-select"
                >
                  <option value="staff">Staff (View assigned properties)</option>
                  <option value="admin">Admin (Full access)</option>
                </select>
              </div>

              {inviteRole === 'staff' && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="invite-assign-section">
                  <label className="flex items-center justify-between cursor-pointer">
                    <span>
                      <span className="block text-sm font-semibold text-slate-700">Assign properties now</span>
                      <span className="block text-xs text-slate-400 mt-0.5">Optional — you can also assign them later from the team list.</span>
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={inviteAssignNow}
                      onClick={() => setInviteAssignNow((v) => !v)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${inviteAssignNow ? 'bg-emerald-500' : 'bg-slate-300'}`}
                      data-testid="invite-assign-toggle"
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${inviteAssignNow ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </label>
                  {inviteAssignNow && (
                    <button
                      type="button"
                      onClick={() => setShowInvitePicker(true)}
                      className="mt-3 w-full h-11 rounded-xl bg-white border border-emerald-200 text-emerald-600 text-sm font-semibold hover:bg-emerald-50 transition-colors"
                      data-testid="invite-choose-properties"
                    >
                      {inviteProps.size > 0 ? `${inviteProps.size} propert${inviteProps.size === 1 ? 'y' : 'ies'} selected` : 'Choose properties'}
                    </button>
                  )}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={creating}
                  className="btn-3d btn-3d-accent flex-1 h-12 rounded-2xl font-semibold"
                  data-testid="send-invite-button"
                >
                  {creating ? 'Sending...' : 'Send Invite'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowInviteForm(false)}
                  className="btn-3d btn-3d-outline h-12 px-6 rounded-2xl"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Pending Invites */}
        {isAdmin && invites.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Mail size={24} className="text-blue-500" />
              Pending Invites
            </h2>
            <div className="space-y-3">
              {invites.map((invite, index) => (
                <motion.div
                  key={invite.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="card-3d p-5"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-slate-900">{invite.name}</h3>
                        <span className="badge-3d badge-warning">
                          <Clock size={14} />
                          Pending
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">{invite.email}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Role: <span className="font-semibold">{invite.role}</span> • Expires {new Date(invite.expiresAt).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => setDeleteInviteId(invite.id)}
                      className="btn-3d h-10 px-4 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 border border-red-200"
                      title="Cancel invite"
                      data-testid={`delete-invite-${invite.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Admin card on top */}
        {adminMembers.length > 0 && (
          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {adminMembers.map((member, index) => memberCard(member, index))}
            </div>
          </div>
        )}

        {/* Team Members (staff only) */}
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Users size={24} className="text-green-500" />
            Team Members ({staffMembers.length})
          </h2>

          {staffMembers.length === 0 ? (
            <div className="card-3d p-8 text-center">
              <Users size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No team members yet</h3>
              <p className="text-slate-500 mb-4">Invite your first team member to get started</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {staffMembers.map((member, index) => memberCard(member, index))}
            </div>
          )}
        </div>

        {cleaningEnabled && (
          <CleaningCompaniesSection
            data={cleaningData}
            onChanged={loadCleaning}
            showAddForm={showCompanyForm}
            setShowAddForm={setShowCompanyForm}
          />
        )}
      </main>

      {/* Invite type chooser */}
      <AlertDialog open={showInviteChooser} onOpenChange={setShowInviteChooser}>
        <AlertDialogContent className="rounded-3xl border-0 shadow-2xl p-0 overflow-hidden max-w-md bg-white">
          <div className="bg-gradient-to-r from-blue-500 to-emerald-500 p-6 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <UserPlus size={32} className="text-white" />
            </div>
            <AlertDialogTitle className="text-2xl font-bold text-white m-0">Who are you adding?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/80 text-sm mt-1 m-0">Choose whether to add an internal team member or an external cleaning company.</AlertDialogDescription>
          </div>
          <div className="p-6 bg-white space-y-3">
            <button
              onClick={() => { setShowInviteChooser(false); setShowCompanyForm(false); setShowInviteForm(true); }}
              className="w-full p-4 rounded-2xl border-2 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 transition-colors flex items-center gap-3 text-left"
              data-testid="choose-team-member"
            >
              <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0"><Users size={22} className="text-emerald-600" /></div>
              <div>
                <p className="font-bold text-slate-900">Team member</p>
                <p className="text-xs text-slate-500">An internal staff member or admin on your account</p>
              </div>
            </button>
            <button
              onClick={() => { setShowInviteChooser(false); setShowInviteForm(false); setShowCompanyForm(true); }}
              className="w-full p-4 rounded-2xl border-2 border-slate-200 hover:border-purple-400 hover:bg-purple-50 transition-colors flex items-center gap-3 text-left"
              data-testid="choose-cleaning-company"
            >
              <div className="w-11 h-11 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0"><SprayCan size={22} className="text-purple-600" /></div>
              <div>
                <p className="font-bold text-slate-900 flex items-center gap-1.5">Cleaning company <Sparkles size={13} className="text-purple-500" /></p>
                <p className="text-xs text-slate-500">An external cleaning team that manages properties you delegate</p>
              </div>
            </button>
          </div>
          <AlertDialogFooter className="p-6 pt-0 bg-white">
            <AlertDialogCancel className="w-full h-12 rounded-xl font-semibold border-2 border-slate-200 hover:bg-slate-50 bg-white text-slate-700">Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!removeUserId} onOpenChange={() => setRemoveUserId(null)}>
        <AlertDialogContent className="rounded-3xl border-0 shadow-2xl p-0 overflow-hidden max-w-md bg-white">
          <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Trash2 size={32} className="text-white" />
            </div>
            <AlertDialogTitle className="text-2xl font-bold text-white m-0">Remove Member?</AlertDialogTitle>
          </div>
          <div className="p-6 bg-white">
            <AlertDialogDescription className="text-center text-slate-600 text-base m-0">
              This will remove their access to your team and all property assignments. They won't be able to log in anymore.
            </AlertDialogDescription>
          </div>
          <AlertDialogFooter className="p-6 pt-0 flex gap-3 bg-white">
            <AlertDialogCancel 
              className="flex-1 h-12 rounded-xl font-semibold border-2 border-slate-200 hover:bg-slate-50 bg-white text-slate-700"
              data-testid="cancel-remove"
            >
              Keep Member
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              className="flex-1 h-12 rounded-xl font-semibold bg-gradient-to-r from-red-500 to-red-600 hover:opacity-90 border-0 text-white"
              data-testid="confirm-remove"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteInviteId} onOpenChange={() => setDeleteInviteId(null)}>
        <AlertDialogContent className="rounded-3xl border-0 shadow-2xl p-0 overflow-hidden max-w-md bg-white">
          <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Trash2 size={32} className="text-white" />
            </div>
            <AlertDialogTitle className="text-2xl font-bold text-white m-0">Cancel Invite?</AlertDialogTitle>
          </div>
          <div className="p-6 bg-white">
            <AlertDialogDescription className="text-center text-slate-600 text-base m-0">
              This will permanently cancel the invitation. The person won't be able to join using the invite link.
            </AlertDialogDescription>
          </div>
          <AlertDialogFooter className="p-6 pt-0 flex gap-3 bg-white">
            <AlertDialogCancel 
              className="flex-1 h-12 rounded-xl font-semibold border-2 border-slate-200 hover:bg-slate-50 bg-white text-slate-700"
              data-testid="cancel-delete-invite"
            >
              Keep Invite
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteInvite}
              className="flex-1 h-12 rounded-xl font-semibold bg-gradient-to-r from-red-500 to-red-600 hover:opacity-90 border-0 text-white"
              data-testid="confirm-delete-invite"
            >
              Cancel Invite
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Property Assignment Modal */}
      <AnimatePresence>
        {assignModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setAssignModal(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-emerald-500 to-blue-500 p-6 text-center">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <PropertiesIcon size={32} className="text-white" strokeWidth={1.8} />
                </div>
                <h2 className="text-2xl font-bold text-white">Assign Properties</h2>
                <p className="text-white/80 text-sm mt-1">to {assignModal.userName}</p>
              </div>

              {/* Search bar */}
              {assignModal.properties.length > 0 && (
                <div className="px-4 pt-4">
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      value={propertySearch}
                      onChange={(e) => setPropertySearch(e.target.value)}
                      placeholder="Search properties by name or address..."
                      className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                      data-testid="assign-property-search"
                    />
                  </div>
                </div>
              )}

              {/* Property List */}
              <div className="flex-1 overflow-y-auto p-4">
                {assignModal.properties.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <PropertiesIcon size={48} className="mx-auto mb-4 text-slate-300" strokeWidth={1.5} />
                    <p>No properties to assign yet.</p>
                    <p className="text-sm">Create properties first.</p>
                  </div>
                ) : (() => {
                  const q = propertySearch.trim().toLowerCase();
                  const filtered = q
                    ? assignModal.properties.filter((p) =>
                        (p.name || '').toLowerCase().includes(q) || (p.address || '').toLowerCase().includes(q))
                    : assignModal.properties;
                  if (filtered.length === 0) {
                    return (
                      <div className="text-center py-8 text-slate-500" data-testid="assign-no-results">
                        <Search size={40} className="mx-auto mb-3 text-slate-300" strokeWidth={1.5} />
                        <p className="text-sm">No properties match "{propertySearch}"</p>
                      </div>
                    );
                  }
                  return (
                  <div className="space-y-2">
                    {filtered.map((property) => {
                      const isAssigned = assignModal.assignedIds.has(property.id);
                      return (
                        <button
                          key={property.id}
                          onClick={() => togglePropertyAssignment(property.id)}
                          className={`w-full p-4 rounded-xl border-2 transition-all flex items-center justify-between ${
                            isAssigned 
                              ? 'border-emerald-500 bg-emerald-50' 
                              : 'border-slate-200 bg-white hover:border-slate-300'
                          }`}
                          data-testid={`assign-property-${property.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                              isAssigned ? 'bg-emerald-500' : 'bg-slate-100'
                            }`}>
                              <PropertiesIcon size={20} className={isAssigned ? 'text-white' : 'text-slate-400'} strokeWidth={1.8} />
                            </div>
                            <div className="text-left">
                              <p className="font-semibold text-slate-900">{property.name}</p>
                              {property.address && (
                                <p className="text-xs text-slate-500">{property.address}</p>
                              )}
                            </div>
                          </div>
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                            isAssigned 
                              ? 'bg-emerald-500 border-emerald-500' 
                              : 'border-slate-300'
                          }`}>
                            {isAssigned && <Check size={14} className="text-white" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  );
                })()}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-slate-100 flex gap-3">
                <button
                  onClick={() => setAssignModal(null)}
                  className="flex-1 h-12 rounded-xl font-semibold border-2 border-slate-200 hover:bg-slate-50 text-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={savePropertyAssignments}
                  disabled={savingAssignments}
                  className="flex-1 h-12 rounded-xl font-semibold bg-gradient-to-r from-emerald-500 to-blue-500 text-white hover:opacity-90 disabled:opacity-50"
                >
                  {savingAssignments ? 'Saving...' : `Save (${assignModal.assignedIds.size} selected)`}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showInvitePicker && (
          <PropertyPickerModal
            title="Assign Properties"
            subtitle="to this new team member"
            properties={allProperties}
            selectedIds={inviteProps}
            onToggle={toggleInviteProp}
            onSave={() => setShowInvitePicker(false)}
            saving={false}
            onClose={() => setShowInvitePicker(false)}
            emptyText="Create properties first, then you can assign them."
          />
        )}
      </AnimatePresence>
    </div>
    </PullToRefresh>
  );
}
